import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { useConfig } from "../../lib/config";
import { toast } from "sonner";
import { Users, FileText, Sparkles, Coins, TrendingUp, BarChart3, CreditCard, Gift, Image as ImageIcon, Upload, Trash2, Tag } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const KPI = ({ label, value, accent, Icon, testid }) => (
  <div
    data-testid={testid}
    className="bg-[#0F1525] border border-[#1E2A3F] rounded-md p-5 hover:border-[#3B82F6]/40 transition-colors"
  >
    <div className="flex items-center justify-between mb-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">{label}</div>
      <div className={`h-8 w-8 rounded-md flex items-center justify-center`} style={{ background: accent + "22", color: accent }}>
        <Icon className="h-4 w-4" strokeWidth={1.75} />
      </div>
    </div>
    <div className="font-display text-4xl tracking-tight" style={{ fontWeight: 800, color: accent }}>
      {value}
    </div>
  </div>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [premiumPkg, setPremiumPkg] = useState(null);
  const [savingMode, setSavingMode] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileRef = useRef(null);
  const { config, reload: reloadPublicConfig } = useConfig();

  useEffect(() => {
    api
      .get("/admin/stats")
      .then((res) => setStats(res.data))
      .catch((e) => toast.error(formatApiError(e)));
    api
      .get("/admin/settings")
      .then((res) => setSettings(res.data))
      .catch((e) => toast.error(formatApiError(e)));
    api
      .get("/admin/pricing")
      .then((res) => {
        const pkg = (res.data.items || []).find((p) => p.id === "premium_unlock");
        setPremiumPkg(pkg || null);
      })
      .catch(() => {});
  }, []);

  const fmtPrice = (pkg) => {
    if (!pkg) return "¥980";
    const cur = (pkg.currency || "jpy").toUpperCase();
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(pkg.amount);
    } catch {
      return `${pkg.amount} ${cur}`;
    }
  };

  const togglePaymentMode = async () => {
    if (!settings) return;
    const next = settings.payment_mode === "stripe" ? "free" : "stripe";
    const priceStr = fmtPrice(premiumPkg);
    if (
      !window.confirm(
        next === "free"
          ? "Switch to FREE mode? All premium unlocks will be granted without Stripe payment."
          : `Switch to STRIPE mode? Premium unlocks will require ${priceStr} payment via Stripe.`
      )
    )
      return;
    setSavingMode(true);
    try {
      const { data } = await api.put("/admin/settings", { payment_mode: next });
      setSettings(data);
      reloadPublicConfig();
      toast.success(`Payment mode → ${next.toUpperCase()}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingMode(false);
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File too large (max 2 MB)");
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSettings((s) => ({ ...(s || {}), logo_data_url: data.logo_data_url }));
      reloadPublicConfig();
      toast.success(`Logo uploaded (${Math.round(data.size / 1024)} KB)`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploadingLogo(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm("Remove the current logo? Default brain icon will be shown.")) return;
    try {
      await api.delete("/admin/upload/logo");
      setSettings((s) => ({ ...(s || {}), logo_data_url: null }));
      reloadPublicConfig();
      toast.success("Logo removed");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!stats) {
    return <div className="p-10 font-mono text-xs uppercase tracking-[0.2em] text-[#64748B]">// Loading…</div>;
  }

  const chartData = stats.by_category.map((c) => ({ name: c.category, value: c.count }));

  return (
    <div className="p-8 lg:p-10">
      <div className="mb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-2">// DASHBOARD</div>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight" style={{ fontWeight: 800 }}>
          Platform overview
        </h1>
      </div>

      {/* Payment mode toggle */}
      {settings && (
        <div
          data-testid="payment-mode-card"
          className={
            "border rounded-md p-5 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 " +
            (settings.payment_mode === "free"
              ? "border-[#00FF94]/40 bg-[#00FF94]/5"
              : "border-[#3B82F6]/40 bg-[#3B82F6]/5")
          }
        >
          <div className="flex items-center gap-4">
            <div
              className="h-10 w-10 rounded-md flex items-center justify-center"
              style={{
                background: settings.payment_mode === "free" ? "#00FF9422" : "#3B82F622",
                color: settings.payment_mode === "free" ? "#00FF94" : "#3B82F6",
              }}
            >
              {settings.payment_mode === "free" ? (
                <Gift className="h-5 w-5" strokeWidth={1.75} />
              ) : (
                <CreditCard className="h-5 w-5" strokeWidth={1.75} />
              )}
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">// PAYMENT MODE</div>
              <div className="font-display text-xl tracking-tight" style={{ fontWeight: 800 }}>
                {settings.payment_mode === "free"
                  ? "FREE — Stripe disabled"
                  : `STRIPE — ${fmtPrice(premiumPkg)} / unlock`}
              </div>
              <div className="text-xs text-[#94A3B8] mt-1">
                {settings.payment_mode === "free"
                  ? "All premium unlocks are granted without payment."
                  : `Users pay ${fmtPrice(premiumPkg)} via Stripe Checkout to unlock premium.`}
              </div>
              <Link
                to="/admin/pricing"
                data-testid="dashboard-edit-pricing"
                className="inline-flex items-center gap-1.5 mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#3B82F6] hover:text-[#60A5FA] hover:underline"
              >
                <Tag className="h-3 w-3" strokeWidth={2} />
                → 金額を編集 / Manage pricing
              </Link>
            </div>
          </div>
          <button
            type="button"
            onClick={togglePaymentMode}
            disabled={savingMode}
            data-testid="toggle-payment-mode"
            className={
              "px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] font-bold border transition-colors disabled:opacity-50 " +
              (settings.payment_mode === "free"
                ? "border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6]/10"
                : "border-[#00FF94] text-[#00FF94] hover:bg-[#00FF94]/10")
            }
          >
            {savingMode
              ? "Switching…"
              : settings.payment_mode === "free"
              ? "→ Re-enable Stripe"
              : "→ Disable Stripe (free)"}
          </button>
        </div>
      )}

      {/* Site logo upload */}
      <div
        data-testid="site-logo-card"
        className="border border-[#1E2A3F] rounded-md p-5 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-[#0F1525]"
      >
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-[1px] bg-[#070B14] border border-[#1E2A3F] flex items-center justify-center overflow-hidden">
            {config?.logo_data_url ? (
              <img src={config.logo_data_url} alt="logo preview" className="max-h-14 max-w-14 object-contain rounded-[1px]" />
            ) : (
              <ImageIcon className="h-7 w-7 text-[#64748B]" strokeWidth={1.25} />
            )}
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">// SITE LOGO</div>
            <div className="font-display text-xl tracking-tight" style={{ fontWeight: 800 }}>
              {config?.logo_data_url ? "Custom logo set" : "Default icon (no logo uploaded)"}
            </div>
            <div className="text-xs text-[#94A3B8] mt-1">
              PNG / JPG / WebP / SVG · max 2 MB · square recommended
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            data-testid="logo-file-input"
            className="hidden"
            onChange={(e) => handleLogoUpload(e.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploadingLogo}
            data-testid="logo-upload-button"
            className="px-4 py-2.5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] font-bold border border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6]/10 transition-colors disabled:opacity-50"
          >
            <Upload className="h-3.5 w-3.5" strokeWidth={2} />
            {uploadingLogo ? "Uploading…" : config?.logo_data_url ? "Replace" : "Upload"}
          </button>
          {config?.logo_data_url && (
            <button
              type="button"
              onClick={handleLogoDelete}
              data-testid="logo-delete-button"
              className="px-3 py-2.5 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] border border-[#1E2A3F] text-[#94A3B8] hover:text-[#EF4444] hover:border-[#EF4444] transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
              Remove
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <KPI testid="kpi-users" label="Total users" value={stats.total_users} accent="#3B82F6" Icon={Users} />
        <KPI testid="kpi-results" label="Total results" value={stats.total_results} accent="#8B5CF6" Icon={FileText} />
        <KPI testid="kpi-premium" label="Premium unlocked" value={stats.premium_results} accent="#00FF94" Icon={Sparkles} />
        <KPI testid="kpi-revenue" label="Revenue (¥)" value={stats.revenue_yen.toLocaleString()} accent="#F59E0B" Icon={Coins} />
        <KPI testid="kpi-conversion" label="Conversion %" value={`${stats.conversion_rate}%`} accent="#06B6D4" Icon={TrendingUp} />
        <KPI testid="kpi-avg-score" label="Avg score" value={stats.avg_overall} accent="#FF2DD0" Icon={BarChart3} />
      </div>

      <div className="bg-[#0F1525] border border-[#1E2A3F] rounded-md p-6 lg:p-8">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">// BY CATEGORY</div>
            <div className="font-display text-xl tracking-tight" style={{ fontWeight: 800 }}>
              Diagnostic completions
            </div>
          </div>
        </div>
        <div className="h-[280px]" data-testid="category-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid stroke="#1E2A3F" strokeDasharray="3 3" />
              <XAxis dataKey="name" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} />
              <Tooltip
                contentStyle={{
                  borderRadius: 8,
                  border: "1px solid #3B82F6",
                  background: "#0F1525",
                  color: "#fff",
                  fontSize: 12,
                }}
              />
              <Bar dataKey="value" fill="#3B82F6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
